import { Box, Button, Flex, SimpleGrid } from "@chakra-ui/react";
import { LRPrivText } from "@verivest/logrocket/src";
import { GroupedInvestmentModel } from "@verivest/shared-components/src/api/models";
import { distributionPreferenceValue } from "@verivest/shared-components/src/utils/distributionPreferenceValue";
import React from "react";
import { dateToMonYYYYUTC, formatToMoney } from "../../../../utils/utils";
import { InvestmentDetailsHeading } from "./InvestmentDetailsHeading";
import { InvestmentDetailsSectionHeading } from "./InvestmentDetailsSectionHeading";
import { ImportedTag } from "../../../../components/general/imported-investment/ImportedTag";
const ContentValue: React.FC = ({ children }) => (
  <Box textStyle="paragraph" mb="24px">
    {children}
  </Box>
);

export interface InvestmentOverviewDetailsProps {
  groupedInvestment: GroupedInvestmentModel;
  openDistributionPreference: () => void;
}

export const InvestmentOverviewDetails: React.FC<
  InvestmentOverviewDetailsProps
> = ({ groupedInvestment, openDistributionPreference }) => {
  return (
    <SimpleGrid columns={[1, 2]}>
      {/* Summary */}
      <Box pr={[null, "12px"]}>
        <InvestmentDetailsSectionHeading
          headingProps={{ alignItems: "center", display: "flex" }}
          withDivider
        >
          Summary
          <ImportedTag
            isImported={groupedInvestment.hasImports}
            containerProps={{ mt: 1 }}
          />
        </InvestmentDetailsSectionHeading>
        {/* First row */}
        <SimpleGrid columns={[1, 2]}>
          <Box>
            <InvestmentDetailsHeading>Inception date</InvestmentDetailsHeading>
            <ContentValue>
              {dateToMonYYYYUTC(
                new Date(groupedInvestment.inceptionDate || ""),
                true,
              )}
            </ContentValue>
          </Box>
          {!groupedInvestment.offering?.hideOwnership && (
            <Box>
              <InvestmentDetailsHeading>Ownership</InvestmentDetailsHeading>
              <ContentValue>
                {(groupedInvestment.ownershipPercentage || 0).toFixed(4)}%
              </ContentValue>
            </Box>
          )}
          {!groupedInvestment.offering?.hideNetIncome && (
            <Box>
              <InvestmentDetailsHeading>Net Income</InvestmentDetailsHeading>
              <ContentValue>
                {formatToMoney(
                  (groupedInvestment.netIncome || []).reduce(
                    (total, current) => total + (current.amount || 0),
                    0,
                  ),
                  true,
                )}
              </ContentValue>
            </Box>
          )}
          {!groupedInvestment.offering?.hideDistributions && (
            <Box>
              <InvestmentDetailsHeading>Distributions</InvestmentDetailsHeading>
              <ContentValue>
                {formatToMoney(groupedInvestment.distributions, true)}
              </ContentValue>
            </Box>
          )}
        </SimpleGrid>
      </Box>
      <Box pl={[null, "12px"]}>
        {/* Settings */}

        <InvestmentDetailsSectionHeading withDivider>
          <Flex className={"justify-between items-center flex-row"}>
            <Box>Settings</Box>
            <Box>
              <Button
                onClick={() => openDistributionPreference()}
                size="xs"
                variant="ghost"
                color="#BE2076"
                fontSize="16px"
              >
                Edit distribution preference
              </Button>
            </Box>
          </Flex>
        </InvestmentDetailsSectionHeading>
        {/* First row */}
        <SimpleGrid columns={[1, 2]} spacing={4}>
          <Box>
            <InvestmentDetailsHeading>Investor</InvestmentDetailsHeading>
            <ContentValue>
              <LRPrivText>
                {groupedInvestment.account?.nameOfRecord || ""}
              </LRPrivText>{" "}
              ({groupedInvestment.account?.accountType || ""})
              {!!groupedInvestment.account?.mnumber && (
                <>
                  <br />
                  {groupedInvestment.account.mnumber}
                </>
              )}
            </ContentValue>
          </Box>
          <Box>
            <InvestmentDetailsHeading>
              Distribution Preference
            </InvestmentDetailsHeading>
            <ContentValue>
              {distributionPreferenceValue(
                groupedInvestment.distributionPreference,
              )}
            </ContentValue>
          </Box>
        </SimpleGrid>
      </Box>
    </SimpleGrid>
  );
};
